/**
 * OPFS quota gate for the Local SQLite attach flow.
 *
 * v1.2 stores attached SQLite databases inside the OPFS SAH pool
 * instead of holding them in WASM heap memory. The ceiling moves from
 * "Chromium WASM heap" (~800 MB) to "browser per-origin storage quota"
 * (typically tens of GB on Chromium, but bounded by free disk space).
 * `navigator.storage.estimate()` is the canonical source for both
 * total quota and current usage.
 *
 * The 80% threshold is a soft cliff: above it we refuse the attach
 * with a user-readable explanation. Below it we allow the attach and
 * let [[attachToOpfs]] react to a real `QuotaExceededError` (rare; the
 * estimate is approximate, and concurrent storage activity from other
 * code paths can shift it between check and use).
 *
 * The check runs CLIENT-side (main thread) before the worker is
 * dispatched — `navigator.storage.estimate()` is available on both
 * main thread and workers, but failing fast on the main thread saves a
 * round-trip and surfaces a cleaner error to the connector card.
 */

export type SqliteAttachQuotaResult =
  | { kind: "ok"; fileSize: number; remainingBytes: number }
  | {
      kind: "block";
      fileSize: number;
      remainingBytes: number;
      reason: string;
    };

/** Fraction of remaining storage budget we'll consume in one attach
 *  before refusing. Keeps headroom for journals + concurrent writes. */
const SAFE_USAGE_FRACTION = 0.8;

/** Bytes of remaining storage budget on this origin. Returns `0` when
 *  `navigator.storage.estimate()` isn't available — callers should
 *  treat that as "unknown budget, refuse" rather than "unlimited." */
export async function remainingStorageBytes(): Promise<number> {
  if (
    typeof navigator === "undefined" ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== "function"
  ) {
    return 0;
  }
  const { quota, usage } = await navigator.storage.estimate();
  return Math.max(0, (quota ?? 0) - (usage ?? 0));
}

/**
 * Decide whether a file of `fileSize` bytes fits into the remaining
 * OPFS quota with [[SAFE_USAGE_FRACTION]] headroom.
 *
 * The `remainingBytes` second argument is exposed for testability; in
 * production callers don't pass it (the helper queries
 * `navigator.storage.estimate` itself).
 */
export async function checkSqliteAttachQuota(
  fileSize: number,
  remainingBytes?: number,
): Promise<SqliteAttachQuotaResult> {
  const budget =
    remainingBytes !== undefined
      ? remainingBytes
      : await remainingStorageBytes();
  const ceiling = Math.floor(budget * SAFE_USAGE_FRACTION);
  if (budget > 0 && fileSize <= ceiling) {
    return { kind: "ok", fileSize, remainingBytes: budget };
  }
  const mb = (n: number) => Math.round(n / (1024 * 1024));
  const gb = (n: number) => (n / (1024 * 1024 * 1024)).toFixed(1);
  const reason =
    budget === 0
      ? "We couldn't check your browser's storage quota. Make sure you're on a Chromium-based browser (Chrome, Edge, Arc, Brave) and try again."
      : `This SQLite file is ${mb(fileSize)} MB but only ${gb(budget)} GB of browser storage is available on this device. Free up space (clear other sites' data or attached databases) and try again.`;
  return { kind: "block", fileSize, remainingBytes: budget, reason };
}
